import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ArrowsClockwise, Play } from "@phosphor-icons/react";
import { apiClient } from "../../lib/api";

// MedexOVA Auth · Admin dashboard for the Rules Engine + Identity Engine +
// Verification Engine. Sales asset and Phase 1 abstraction verification tool.
// Bible §"Trust is the product." Vision 2026 §5 & §12.
//
// Read-only surface. Zero mutations. Every displayed value comes from the four
// Phase 1 preview endpoints under /api/auth/*.

const C = {
  ink: "#0F172A",
  dim: "#64748B",
  line: "#E2E0D8",
  bone: "#F7F5EF",
  forest: "#2B4C3B",
  gold: "#B08A2E",
};

const DEFAULT_TRIP_JSON = JSON.stringify(
  { source: "broker:kaizen", vehicle_type: "sedan" },
  null,
  2
);

export default function AStatAuthPanel() {
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [providers, setProviders] = useState([]);
  const [fleet, setFleet] = useState({
    count: 0,
    average: 0,
    verified_pct: 0,
    by_bucket: { "0-39": 0, "40-59": 0, "60-79": 0, "80-100": 0 },
  });

  // Playground state
  const [tripInput, setTripInput] = useState(DEFAULT_TRIP_JSON);
  const [previewOut, setPreviewOut] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pkgR, provR, fleetR] = await Promise.allSettled([
          apiClient.get("/auth/rules/packages"),
          apiClient.get("/auth/verification/providers"),
          apiClient.get("/auth/identity/fleet-summary"),
        ]);
        if (cancelled) return;
        if (pkgR.status === "fulfilled") setPackages(pkgR.value.data?.packages || []);
        if (provR.status === "fulfilled") setProviders(provR.value.data?.providers || []);
        if (fleetR.status === "fulfilled") setFleet(fleetR.value.data || {});
      } catch (e) {
        toast.error("Could not load MedexOVA Auth state");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const runPreview = async () => {
    setPreviewing(true);
    setPreviewOut(null);
    try {
      let parsed;
      try {
        parsed = JSON.parse(tripInput || "{}");
      } catch {
        toast.error("Trip JSON is not valid");
        setPreviewing(false);
        return;
      }
      const r = await apiClient.post("/auth/rules/preview", parsed);
      setPreviewOut(r.data?.workflow || null);
    } catch (e) {
      toast.error("Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const totalWeight = fleet.count > 0 ? fleet.count : 1;

  return (
    <div className="space-y-4" data-testid="astat-auth-panel">
      {/* Header */}
      <div className="rounded-2xl bg-white border p-5" style={{ borderColor: C.line }}>
        <div className="flex items-start gap-3">
          <div
            className="w-11 h-11 rounded-xl grid place-items-center"
            style={{ background: C.forest }}
          >
            <ShieldCheck size={22} weight="bold" color="#fff" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.forest }}>
                MedexOVA Auth
              </div>
              <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.gold }}>
                · Phase 1
              </div>
            </div>
            <div className="text-lg font-heading font-bold mt-0.5" style={{ color: C.ink }}>
              Verified Transportation Infrastructure
            </div>
            <div className="text-xs mt-1" style={{ color: C.dim }}>
              Every trip composed through the Rules Engine. Every driver scored through the Identity Engine.
              Every verification routed through pluggable providers. New verticals ship as JSON, not code.
            </div>
          </div>
        </div>
      </div>

      {/* Three headline KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Kpi
          testId="astat-kpi-packages"
          label="Rule Packages loaded"
          value={loading ? "—" : String(packages.length)}
          sub="One JSON per vertical"
        />
        <Kpi
          testId="astat-kpi-providers"
          label="Verification Providers"
          value={loading ? "—" : String(providers.length)}
          sub="Pluggable adapters behind one interface"
        />
        <Kpi
          testId="astat-kpi-fleet"
          label="Fleet Credential Score"
          value={loading ? "—" : `${fleet.average || 0}`}
          sub={
            fleet.count
              ? `${fleet.verified_pct}% verified · ${fleet.count} drivers`
              : "No drivers scored yet"
          }
        />
      </div>

      {/* Rule Packages */}
      <div className="rounded-2xl bg-white border p-4" style={{ borderColor: C.line }} data-testid="astat-rule-packages">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.dim }}>
              Loaded Rule Packages
            </div>
            <div className="text-sm mt-0.5" style={{ color: C.ink }}>
              Higher priority wins when a trip matches multiple packages.
            </div>
          </div>
          <div className="text-[11px]" style={{ color: C.dim }}>
            Drop a new JSON into <code>/app/backend/rule_packages/</code> — no code changes required.
          </div>
        </div>
        {loading ? (
          <div className="text-xs" style={{ color: C.dim }}>Loading…</div>
        ) : packages.length === 0 ? (
          <div className="text-xs" style={{ color: C.dim }}>No packages loaded.</div>
        ) : (
          <ul className="divide-y" style={{ borderColor: C.line }}>
            {packages.map((p) => (
              <li key={p.id} className="py-2 flex items-start gap-3" data-testid={`astat-package-${p.id}`}>
                <div
                  className="text-[10px] font-bold rounded-full px-2 py-0.5"
                  style={{ background: p.priority >= 200 ? "#DCFCE7" : p.priority >= 150 ? "#FEF3C7" : "#E0E7FF", color: C.ink }}
                >
                  P{p.priority}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate" style={{ color: C.ink }}>
                    {p.name || p.id}
                  </div>
                  <div className="text-[11px] truncate" style={{ color: C.dim }}>
                    {p.description || `id: ${p.id}`}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Verification Providers */}
      <div className="rounded-2xl bg-white border p-4" style={{ borderColor: C.line }} data-testid="astat-providers">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.dim }}>
              Verification Providers
            </div>
            <div className="text-sm mt-0.5" style={{ color: C.ink }}>
              Behind one canonical interface — swap a vendor by writing one file.
            </div>
          </div>
        </div>
        {providers.length === 0 ? (
          <div className="text-xs" style={{ color: C.dim }}>None registered.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {providers.map((p) => (
              <div
                key={p.modality}
                className="rounded-lg border p-3"
                style={{ borderColor: C.line }}
                data-testid={`astat-provider-${p.modality}`}
              >
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.forest }}>
                  {p.modality}
                </div>
                <div className="text-xs font-semibold mt-1" style={{ color: C.ink }}>
                  {p.provider_id}
                </div>
                {p.provider_id?.startsWith("stub:") && (
                  <div className="text-[10px] mt-1" style={{ color: C.gold }}>
                    Phase 1 stub — real adapter replaces in Phase 2
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fleet Credential Score buckets */}
      <div className="rounded-2xl bg-white border p-4" style={{ borderColor: C.line }} data-testid="astat-fleet-buckets">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.dim }}>
              Fleet Credential Score distribution
            </div>
            <div className="text-sm mt-0.5" style={{ color: C.ink }}>
              Verified drivers (score ≥ 80) are portable across every vertical.
            </div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {["80-100", "60-79", "40-59", "0-39"].map((b) => {
            const n = fleet?.by_bucket?.[b] || 0;
            const pct = Math.round(100 * n / totalWeight);
            const color = b === "80-100" ? C.forest : b === "60-79" ? "#4B7A4E" : b === "40-59" ? C.gold : "#9CA3AF";
            return (
              <div key={b} className="rounded-lg p-3" style={{ background: C.bone }} data-testid={`astat-bucket-${b}`}>
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color }}>
                  {b}
                </div>
                <div className="text-xl font-heading font-bold mt-1" style={{ color: C.ink }}>{n}</div>
                <div className="text-[10px]" style={{ color: C.dim }}>{pct}% of fleet</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Workflow playground — the sales-demo showpiece */}
      <div className="rounded-2xl bg-white border p-4" style={{ borderColor: C.line }} data-testid="astat-playground">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.dim }}>
              Compose a workflow
            </div>
            <div className="text-sm mt-0.5" style={{ color: C.ink }}>
              Paste any trip shape. See the exact identity checks, verifications, credentials, ceremonies, proofs, payer rules, and SLA that will apply.
            </div>
          </div>
          <button
            onClick={runPreview}
            disabled={previewing}
            data-testid="astat-preview-run"
            className="text-xs font-bold px-3 py-1.5 rounded-full disabled:opacity-50 inline-flex items-center gap-1.5"
            style={{ background: C.forest, color: "#fff" }}
          >
            {previewing ? <ArrowsClockwise size={12} className="animate-spin" /> : <Play size={12} weight="fill" />}
            {previewing ? "Composing…" : "Preview"}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: C.dim }}>
              Trip (JSON)
            </div>
            <textarea
              value={tripInput}
              onChange={(e) => setTripInput(e.target.value)}
              data-testid="astat-preview-input"
              rows={12}
              spellCheck={false}
              className="w-full text-xs font-mono border rounded-lg p-2"
              style={{ borderColor: C.line, background: C.bone, color: C.ink }}
            />
            <div className="flex flex-wrap gap-1 mt-1">
              {[
                { label: "NEMT", body: { source: "broker:kaizen" } },
                { label: "Consumer", body: { source: "consumer" } },
                { label: "Rx", body: { source: "manual", cargo_type: "prescription" } },
                { label: "VA", body: { source: "broker:va", demand_source: "va" } },
                { label: "Test Kit", body: { cargo_type: "test_kit" } },
              ].map((s) => (
                <button
                  key={s.label}
                  onClick={() => setTripInput(JSON.stringify(s.body, null, 2))}
                  data-testid={`astat-preview-sample-${s.label.toLowerCase().replace(/\s+/g,'-')}`}
                  className="text-[10px] font-bold px-2 py-0.5 rounded-full border"
                  style={{ borderColor: C.line, color: C.forest }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: C.dim }}>
              Composed workflow
            </div>
            {previewOut ? (
              <div className="rounded-lg border p-3 text-xs space-y-2" style={{ borderColor: C.line, background: C.bone }} data-testid="astat-preview-out">
                <Row label="Package" value={<span className="font-mono font-bold">{previewOut.package_id}</span>} />
                <Row label="Name" value={previewOut.package_name} />
                <Row label="Identity checks" value={<Chips items={previewOut.identity_checks} />} />
                <Row label="Verifications" value={<Chips items={previewOut.verifications} />} />
                <Row label="Driver credentials" value={<Chips items={previewOut.driver_credentials} />} />
                <Row label="Pickup ceremony" value={<span className="font-mono">{previewOut.pickup_ceremony}</span>} />
                <Row label="Dropoff ceremony" value={<span className="font-mono">{previewOut.dropoff_ceremony}</span>} />
                <Row label="Proof artifacts" value={<Chips items={previewOut.proof_artifacts} />} />
                <Row label="Payer rules" value={<pre className="text-[10px] font-mono whitespace-pre-wrap">{JSON.stringify(previewOut.payer_rules, null, 2)}</pre>} />
                <Row label="SLA" value={<pre className="text-[10px] font-mono whitespace-pre-wrap">{JSON.stringify(previewOut.sla, null, 2)}</pre>} />
              </div>
            ) : (
              <div className="text-xs italic" style={{ color: C.dim }}>Press Preview to compose the workflow.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ testId, label, value, sub }) {
  return (
    <div className="rounded-2xl bg-white border p-4" style={{ borderColor: C.line }} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.dim }}>{label}</div>
      <div className="text-3xl font-heading font-bold mt-1" style={{ color: C.ink }}>{value}</div>
      <div className="text-[11px] mt-1" style={{ color: C.dim }}>{sub}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-4 gap-2">
      <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.dim }}>{label}</div>
      <div className="col-span-3">{value}</div>
    </div>
  );
}

function Chips({ items }) {
  if (!items || items.length === 0) return <span className="text-[11px]" style={{ color: C.dim }}>—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((it, i) => (
        <span key={i} className="text-[10px] font-mono rounded px-1.5 py-0.5" style={{ background: "#E0E7FF", color: C.ink }}>
          {it}
        </span>
      ))}
    </div>
  );
}
